import * as ents from '@attain-apps/entities'
import type * as chartJs from 'chart.js'

import * as colors from '../../../colors'

/**
 * Add opacity to a hex color
 */
function addOpacityToColor(hexColor: string, opacity: number): string {
  // Remove # if present
  const hex = hexColor.replace('#', '')

  // Take only the first 6 characters (RGB), ignore existing alpha if present
  const rgb = hex.substring(0, 6)

  // Convert opacity (0-1) to hex (00-FF)
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()

  return `#${rgb}${alpha}`
}

/**
 * Blend multiple hex colors together by averaging their RGB values
 */
function blendColors(hexColors: string[], opacity: number): string {
  if (hexColors.length === 0) {
    return '#9CA3AF66' // Default gray fallback
  }

  if (hexColors.length === 1) {
    return addOpacityToColor(hexColors[0], opacity)
  }

  // Parse all colors to RGB
  const rgbValues = hexColors.map(hexColor => {
    const hex = hexColor.replace('#', '').substring(0, 6)
    const red = parseInt(hex.substring(0, 2), 16)
    const green = parseInt(hex.substring(2, 4), 16)
    const blue = parseInt(hex.substring(4, 6), 16)
    return { b: blue, g: green, r: red }
  })

  // Average the RGB values
  const avgR = Math.round(
    rgbValues.reduce((sum, rgb) => sum + rgb.r, 0) / rgbValues.length,
  )
  const avgG = Math.round(
    rgbValues.reduce((sum, rgb) => sum + rgb.g, 0) / rgbValues.length,
  )
  const avgB = Math.round(
    rgbValues.reduce((sum, rgb) => sum + rgb.b, 0) / rgbValues.length,
  )

  // Convert back to hex
  const r = avgR.toString(16).padStart(2, '0')
  const g = avgG.toString(16).padStart(2, '0')
  const b = avgB.toString(16).padStart(2, '0')

  // Add opacity
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()

  return `#${r}${g}${b}${alpha}`
}

/**
 * Transform data from
 * {@link ents.core.display.chart.data.Of<ents.core.display.chart.Type.Venn>} to
 * {@link chartJs.ChartData<'venn'>}
 */
export function getData(
  data: ents.core.display.chart.data.Of<ents.core.display.chart.Type.Venn>,
): chartJs.ChartData<'venn'> {
  const palette = colors.createPalette(ents.core.display.chart.Type.Venn)
  const sets = data.sets ?? []
  const intersections = data.intersections ?? []

  // Early return when no sets provided
  if (!sets.length) {
    return { datasets: [] }
  }

  // Build a stable key for each set and a mapping to its color
  const setKeyByIndex = new Map<number, string>()
  const setKeyByCodeOrName = new Map<string, string>()
  const setColorByKey = new Map<string, string>()

  sets.forEach((set, index) => {
    const preferredKey = set.code ?? set.name ?? `Set ${index + 1}`
    setKeyByIndex.set(index, preferredKey)
    if (set.code) {
      setKeyByCodeOrName.set(String(set.code), preferredKey)
    }
    if (set.name) {
      setKeyByCodeOrName.set(set.name, preferredKey)
    }
    const baseColor = set.color ?? palette.getOrAssignColorFor(preferredKey)
    // Add opacity to base set colors (similar to the design: semi-transparent)
    const colorWithOpacity = addOpacityToColor(baseColor, 0.6)
    setColorByKey.set(preferredKey, colorWithOpacity)
  })

  // Helper to normalize intersection set references to our computed keys
  const normalizeSetKeys = (keys: readonly string[] | undefined): string[] =>
    (keys ?? []).map(key => setKeyByCodeOrName.get(key) ?? key)

  // Compose chart data items (single sets + intersections)
  const items: {
    sets: string[]
    value: number
    label?: string
    name?: string
  }[] = []
  const baseSetDisplayNames: string[] = []
  // First, the base sets
  sets.forEach((set, index) => {
    const key =
      setKeyByIndex.get(index) ?? set.code ?? set.name ?? `Set ${index + 1}`
    const displayName = set.name ?? set.code ?? `Set ${index + 1}`
    baseSetDisplayNames.push(displayName)
    items.push({
      label: displayName,
      name: displayName,
      sets: [key],
      value: set.value ?? 0,
    })
  })
  // Then, intersections
  intersections.forEach(intersection => {
    const keys = normalizeSetKeys(intersection.of)
    const displayName =
      intersection.name ??
      (keys.length > 1 ? keys.join(' ∩ ') : (keys[0] ?? ''))
    items.push({
      label: displayName,
      name: displayName,
      sets: keys,
      value: intersection.value ?? 0,
    })
  })

  // Assign background colors: use set color for single-set entries; blend colors for overlaps
  const backgroundColor: string[] = items.map(item => {
    if ((item.sets?.length ?? 0) === 1) {
      const key = item.sets[0]
      return setColorByKey.get(key) ?? palette.getOrAssignColorFor(key)
    }
    // Blend colors from all sets involved in the intersection
    const setColors = item.sets
      .map(key => setColorByKey.get(key))
      .filter((color): color is string => color !== undefined)

    // Use a higher opacity for intersections to make the blend more visible
    return blendColors(setColors, 0.7)
  })

  const dataset: chartJs.ChartDataset<'venn'> = {
    backgroundColor,
    // Cast to the plugin data shape (ISet), which matches `{ sets, value, label }`
    data: items as unknown as chartJs.ChartDataset<'venn'>['data'],
    // Ensure legend never shows undefined
    label: data.name ?? 'Venn',
  }

  // Provide top-level labels as a best-effort hint for libraries that read them
  return { datasets: [dataset], labels: baseSetDisplayNames }
}
